import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  AlertTitle,
  Divider
} from '@mui/material';
import { testWooCommerceAPI } from '../config/woocommerce';

const TestWooCommerce = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [productId, setProductId] = useState('');
  const [error, setError] = useState('');
  const [tested, setTested] = useState(false);

  const runTest = async (id) => {
    if (!id) {
      setError('Please enter a product ID');
      return;
    }
    
    setLoading(true);
    setTested(true);
    setError('');
    
    try {
      const testResult = await testWooCommerceAPI(id);
      setResult(testResult);
      console.log('Test Result:', testResult);
    } catch (error) {
      console.error('Test failed:', error);
      setError(error.message || 'An error occurred while testing the API');
      setResult({ 
        success: false, 
        error: error.message,
        details: error.details || error.stack 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runTest(productId);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runTest(productId);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        WooCommerce API Tester
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Test Product Variations
        </Typography>
        
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{ 
            display: 'flex', 
            gap: 2,
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <TextField
            label="Product ID"
            variant="outlined"
            size="small"
            value={productId}
            onChange={(e) => setProductId(e.target.value.replace(/\D/g, ''))}
            onKeyPress={handleKeyPress}
            placeholder="Enter product ID"
            disabled={loading}
            sx={{ flex: 1 }}
            error={!!error}
            helperText={error || ' '}
          />
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading || !productId.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Testing...' : 'Test API'}
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Enter a product ID to test the WooCommerce API connection and fetch variations.
        </Typography>
      </Paper>

      {tested && (
        <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={24} />
              <Typography>Testing API connection...</Typography>
            </Box>
          ) : result ? (
            <Box>
              <Alert 
                severity={result.success ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                <AlertTitle>
                  {result.success ? 'Success' : 'Test Failed'}
                </AlertTitle>
                {result.success 
                  ? 'Successfully connected to the WooCommerce API.'
                  : result.error || 'An unknown error occurred.'
                }
              </Alert>
              
              {result.product && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Product Information
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                    <Box component="pre" sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.85rem',
                      m: 0
                    }}>
                      {JSON.stringify(result.product, null, 2)}
                    </Box>
                  </Paper>
                </Box>
              )}
              
              {result.variations && result.variations.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Variations ({result.variations.length})
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box component="pre" sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.85rem',
                      m: 0,
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(result.variations, null, 2)}
                    </Box>
                  </Paper>
                </Box>
              )}
              
              {result.details && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Error Details
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box component="pre" sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '0.75rem',
                      color: 'error.main',
                      m: 0
                    }}>
                      {JSON.stringify(result.details, null, 2)}
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No test results to display. Enter a product ID and click "Test API" to begin.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TestWooCommerce;
